// Insurance — Company → Management → Insurance. Owner, 2026-09-26: "Let's
// get this [the MEWG LLC master insurance spreadsheet] into a disk called
// Insurance under Company and Management … same as we did with Leases —
// need to make sure to have all the proper dates, expirations etc."
//
// Mirrors lib/leases.ts's own shape: one fetch under ONE key covering every
// policy (not per-clinic), because a policy is not clinic-scoped the way a
// lease is — it can cover several locations at once (the spreadsheet's own
// "covers 4 locations"), so `locations` rides along INSIDE each policy row
// (src/insurance.js's own listCompanyInsurance embeds it) rather than being
// joined client-side the way leaseGroups joins lease + lease_document.
import { authedGet, authedWrite } from './apiClient';
import { persistResource } from './store';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from './locations';
import { daysBetweenIso } from './civilDate';

export const INSURANCE_KEY = 'company-insurance';

export type InsuranceStatus = 'ACTIVE' | 'RENEWING' | 'EXPIRED' | 'CANCELLED';

export const INSURANCE_STATUSES: InsuranceStatus[] = ['ACTIVE', 'RENEWING', 'EXPIRED', 'CANCELLED'];

export const INSURANCE_STATUS_LABEL: Record<InsuranceStatus, string> = {
  ACTIVE: 'Active',
  RENEWING: 'Renewing',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

// Owner, 2026-09-26 (loading the real set): about 30 of the loaded rows are
// past terms back to 2019, kept because their policy papers exist. EXPIRED
// and CANCELLED are "past" — resolved, done, never need a renewal nag —
// exactly the same two-bucket split Leases draws for its own alert-excluded
// statuses (src/lease_alerts.js's EXCLUDED_STATUSES), one status pair later.
export const INSURANCE_PAST_STATUSES: InsuranceStatus[] = ['EXPIRED', 'CANCELLED'];

export type InsuranceDocumentKind =
  | 'POLICY' | 'CERTIFICATE' | 'ENDORSEMENT' | 'RENEWAL' | 'INVOICE' | 'CLAIM' | 'LETTER' | 'OTHER';

export const INSURANCE_DOCUMENT_KINDS: InsuranceDocumentKind[] = [
  'POLICY', 'CERTIFICATE', 'ENDORSEMENT', 'RENEWAL', 'INVOICE', 'CLAIM', 'LETTER', 'OTHER',
];

export const INSURANCE_DOCUMENT_KIND_LABEL: Record<InsuranceDocumentKind, string> = {
  POLICY: 'Policy',
  CERTIFICATE: 'Certificate',
  ENDORSEMENT: 'Endorsement',
  RENEWAL: 'Renewal',
  INVOICE: 'Invoice',
  CLAIM: 'Claim',
  LETTER: 'Letter',
  OTHER: 'Other',
};

export type InsuranceDocumentDateBasis = 'SIGNED' | 'EFFECTIVE' | 'EMAIL' | 'FILE_NAME' | 'UNKNOWN';

export const INSURANCE_DOCUMENT_DATE_BASES: InsuranceDocumentDateBasis[] = [
  'SIGNED', 'EFFECTIVE', 'EMAIL', 'FILE_NAME', 'UNKNOWN',
];

export const INSURANCE_DOCUMENT_DATE_BASIS_LABEL: Record<InsuranceDocumentDateBasis, string> = {
  SIGNED: 'Signed',
  EFFECTIVE: 'Effective',
  EMAIL: 'Email date',
  FILE_NAME: 'From file name',
  UNKNOWN: 'Unknown',
};

export interface InsurancePolicyLocation {
  locationCode: string;
  payrollBasis: string | null;
  note: string | null;
}

export interface InsurancePolicyRow {
  id: number;
  coverageType: string;
  carrier: string | null;
  policyNumber: string | null;
  namedInsured: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  limits: string | null;
  deductible: string | null;
  bpp: string | null;
  premium: number | null;
  broker: string | null;
  brokerContact: string | null;
  status: InsuranceStatus;
  notes: string | null;
  source: string | null;
  locations: InsurancePolicyLocation[];
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface InsuranceDocumentRow {
  id: number;
  policyId: number | null;
  locationCode: string | null;
  kind: InsuranceDocumentKind;
  title: string;
  docDate: string | null;
  dateBasis: InsuranceDocumentDateBasis | null;
  driveFileId: string | null;
  driveUrl: string | null;
  url: string | null;
  sourcePath: string | null;
  sha256: string | null;
  note: string | null;
  addedAt: string;
  addedBy: string | null;
}

export interface InsuranceData {
  policies: InsurancePolicyRow[];
  documents: InsuranceDocumentRow[];
}

export function fetchInsurance(): Promise<InsuranceData> {
  return authedGet('/api/v1/company/insurance');
}

export async function reloadInsurance(): Promise<void> {
  const data = await fetchInsurance();
  await persistResource(INSURANCE_KEY, data);
}

function byDocDateThenId(a: InsuranceDocumentRow, b: InsuranceDocumentRow): number {
  if (a.docDate !== b.docDate) {
    if (a.docDate === null) return 1;
    if (b.docDate === null) return -1;
    return a.docDate < b.docDate ? -1 : 1;
  }
  return a.id - b.id;
}

/** Every document filed against ONE policy, oldest first. The server
 * already returns the whole list in that order (doc_date nulls last, then
 * id) — this re-sorts anyway rather than trusting fetch order to survive
 * every future caller, the same "guards over trust" lib/leases.ts's own
 * documentsFor follows. */
export function documentsForPolicy(documents: InsuranceDocumentRow[], policyId: number): InsuranceDocumentRow[] {
  return documents.filter((d) => d.policyId === policyId).sort(byDocDateThenId);
}

/**
 * Documents naming no policy at all, oldest first — the "Certificates &
 * other documents" panel below the main table. Two real shapes land here
 * (owner correction, 2026-09-26, while loading the real set):
 *   - `locationCode` set: a certificate of insurance issued to one
 *     landlord for one clinic, with no single policy it maps to cleanly.
 *   - `locationCode` also null: a COMPANY-LEVEL document — a combined
 *     certificate, a WC exemption certificate, a broker summary spreadsheet
 *     — covering several policies (and often several clinics) at once.
 * Both render in the same panel; the row itself shows the clinic name when
 * `locationCode` is set, or nothing when it is company-wide.
 */
export function unattachedDocuments(documents: InsuranceDocumentRow[]): InsuranceDocumentRow[] {
  return documents.filter((d) => d.policyId === null).sort(byDocDateThenId);
}

/** The latest RENEWAL instrument filed against a policy — null when none has
 * been filed. Mirrors lib/leases.ts's own latestRenewal, simplified: leases
 * pick between EXTENSION and AMENDMENT because a lease's term can move
 * without a document literally called "renewal"; a policy's own kind list
 * has RENEWAL itself, so there is no second kind to prefer over it. */
export function latestRenewalDocument(documents: InsuranceDocumentRow[], policyId: number): InsuranceDocumentRow | null {
  return documentsForPolicy(documents, policyId)
    .filter((d): d is InsuranceDocumentRow & { docDate: string } => d.kind === 'RENEWAL' && d.docDate !== null)
    .sort((a, b) => (a.docDate < b.docDate ? 1 : a.docDate > b.docDate ? -1 : b.id - a.id))[0] ?? null;
}

/** Clinic names a policy covers, in LOCATION_ORDER — for the table's
 * "Locations" column and the per-policy page's fact table. */
export function policyLocationNames(policy: InsurancePolicyRow): string[] {
  const codes = new Set(policy.locations.map((l) => l.locationCode));
  return LOCATION_ORDER.filter((code) => codes.has(code)).map((code) => LOCATION_NAMES[code as LocationCode] ?? code);
}

export type InsurancePolicyWrite = {
  coverageType: string;
  carrier: string;
  policyNumber: string;
  namedInsured: string;
  effectiveDate: string;
  expirationDate: string;
  limits: string;
  deductible: string;
  bpp: string;
  premium: string;
  broker: string;
  brokerContact: string;
  status: InsuranceStatus;
  notes: string;
  source: string;
};

export async function addInsurancePolicy(body: InsurancePolicyWrite): Promise<{ policy: InsurancePolicyRow }> {
  return authedWrite('POST', '/api/v1/company/insurance', body);
}

export async function updateInsurancePolicy(id: number, patch: Partial<InsurancePolicyWrite>): Promise<{ policy: InsurancePolicyRow }> {
  return authedWrite('PATCH', `/api/v1/company/insurance/${id}`, patch);
}

export async function removeInsurancePolicy(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/insurance/${id}`, {});
}

/** The write shape for one covered-clinic row — plain strings (the form's
 * own controlled-input value), unlike InsurancePolicyLocation's read shape
 * (`string | null`); the server normalises a blank to null the same way
 * every other write in this app does. */
export type InsurancePolicyLocationWrite = { locationCode: string; payrollBasis: string; note: string };

/** Replaces the policy's whole covered-clinic list — the client always
 * sends the complete array (never a partial add/remove), matching how
 * src/sync/version.js's insurance_policy.set_locations itself replaces the
 * whole set in one write. */
export async function setInsurancePolicyLocations(
  id: number,
  locations: InsurancePolicyLocationWrite[],
): Promise<{ locations: InsurancePolicyLocation[] }> {
  return authedWrite('PUT', `/api/v1/company/insurance/${id}/locations`, { locations });
}

export type InsuranceDocumentWrite = {
  kind: InsuranceDocumentKind;
  title: string;
  docDate: string;
  dateBasis: InsuranceDocumentDateBasis | '';
  driveFileId: string;
  url: string;
  sourcePath: string;
  sha256: string;
  note: string;
};

/** A document filed to a policy that already has a row. */
export async function addInsuranceDocument(policyId: number, body: InsuranceDocumentWrite): Promise<{ document: InsuranceDocumentRow }> {
  return authedWrite('POST', `/api/v1/company/insurance/${policyId}/documents`, body);
}

/** A document with no single policy it maps to cleanly. `locationCode`
 * empty files a COMPANY-LEVEL document (covers several policies at once);
 * given, it files a certificate to that one clinic. */
export async function addInsuranceDocumentForLocation(
  locationCode: string,
  body: InsuranceDocumentWrite,
): Promise<{ document: InsuranceDocumentRow }> {
  return authedWrite('POST', '/api/v1/company/insurance/documents', { ...body, locationCode });
}

export async function updateInsuranceDocument(id: number, body: InsuranceDocumentWrite): Promise<{ document: InsuranceDocumentRow }> {
  return authedWrite('PATCH', `/api/v1/company/insurance/documents/${id}`, body);
}

export async function removeInsuranceDocument(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/insurance/documents/${id}`, {});
}

// --- expiration alerts -------------------------------------------------------
//
// Owner, 2026-09-26: "make sure to have all the proper dates, expirations
// etc." src/insurance_alerts.js carries the 60/30-DAY rule server-side (by
// day count, for the actual emails; this build's own judgment call — see
// that file's header for why days, not the 12/6 MONTHS Leases uses); this
// is the client's own read of it, for the My Desk "Needs attention" panel
// and the Insurance table's own hint under "Expires" — OWNER/DIRECTOR only,
// the same access Insurance has everywhere else (insuranceRoleGate).

/** The window: a policy within this many days of expiring shows up. */
export const INSURANCE_ATTENTION_DAYS = 60;
/** Inside this many days, the hint/line gets the warn treatment. */
export const INSURANCE_WARN_DAYS = 30;

/** Whole calendar days from `today` to `expirationDate` — client/src/lib/
 * civilDate.ts's own daysBetweenIso, no `Date` object anywhere (INV-8: the
 * built site carries no timezone API at all, not even by way of
 * `Date.UTC`, which designlaw.test.ts scans the built output for by name).
 * Can return a negative number for an expiration already past. */
export function policyDaysLeft(expirationDate: string, today: string): number {
  return daysBetweenIso(today, expirationDate);
}

/** The policies the table shows by default — everything but EXPIRED/
 * CANCELLED (owner, 2026-09-26: loading ~30 past terms back to 2019
 * because their papers exist, but the table should open on what is
 * actually in force). */
export function currentPolicies(policies: InsurancePolicyRow[]): InsurancePolicyRow[] {
  return policies.filter((p) => !INSURANCE_PAST_STATUSES.includes(p.status));
}

/** EXPIRED/CANCELLED policies, newest first — behind the table's "Show past
 * policies" chip. */
export function pastPolicies(policies: InsurancePolicyRow[]): InsurancePolicyRow[] {
  return policies
    .filter((p) => INSURANCE_PAST_STATUSES.includes(p.status))
    .sort((a, b) => (b.expirationDate ?? '').localeCompare(a.expirationDate ?? ''));
}

/** Active/renewing policies with an expiration_date that is today or later
 * and within INSURANCE_ATTENTION_DAYS, soonest first. EXPIRED/CANCELLED are
 * always excluded — already resolved, so they stop nagging. Mirrors
 * src/insurance_alerts.js's insuranceAlertsDue's own status/date filter,
 * for the panel line and its hasContent peek
 * (client/src/desk/panelCatalog.tsx). */
export function policiesNeedingAttention(policies: InsurancePolicyRow[], today: string): InsurancePolicyRow[] {
  return policies
    .filter((p): p is InsurancePolicyRow & { expirationDate: string } => (
      p.expirationDate !== null && !INSURANCE_PAST_STATUSES.includes(p.status)
    ))
    .filter((p) => {
      const days = policyDaysLeft(p.expirationDate, today);
      return days >= 0 && days <= INSURANCE_ATTENTION_DAYS;
    })
    .sort((a, b) => policyDaysLeft(a.expirationDate as string, today) - policyDaysLeft(b.expirationDate as string, today));
}
